/* eslint-disable no-confusing-arrow */
import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from '@emotion/styled';

import { rem } from '../../../utilities/style';
import { colors } from '../../../theme/colors';
import Image from '../../Image';
import { media } from '../../../theme/media';
import { headers, body } from '../../../styles/type';
import { containerStyles } from '../../../styles/layout';

import Button from '../../buttons/Button';
import ButtonGroup from '../../ButtonGroup';
import ButtonText from '../../buttons/ButtonText';
import HeroAnimation from './HeroAnimation';
import TextRenderer from '../../TextRenderer';

const ImageContainer = styled.div`
  width: 40rem;
  max-width: 100%;
  margin-bottom: ${rem(16)};

  ${media('>small')} {
    flex: 0 0 32%;
    height: auto;
  }

  > .gatsby-image-wrapper {
    ${media('>small')} {
      max-width: 100%;
    }
  }
`;

const Container = styled.div`
padding: ${rem(24)} 0;
background: ${colors.white};
overflow: hidden;

${media('>small')} {
  padding: ${rem(40)} 0 ${rem(30)};
}
`;
const HeroImage = styled(Image)`
${media('>large')} {
  max-width: 100%;
}
`;

const VideoPlayButton = styled(Image)`
display: flex;
visibility: visible;
width: 25px;
height: auto;
padding-left: -2px;
margin-left: 9px;
`;

const MobileHeroImage = styled(HeroImage)`
${media('>small')} {
  display: none;
}
`;

const MobileHeroAnimation = styled(HeroAnimation)``;

const ContentWrapper = styled.div`
    ${containerStyles.regular}

    ${media('>small')} {
      display: grid;
      grid-template-columns: 50% 50%;
      gap: ${(props) => props.heroImage ? rem(3) : rem(40)};
    }

    ${media('>smedium')} {
      gap: ${(props) => props.heroImage ? rem(6) : rem(80)};
    }

    ${media('>medium')} {
      gap: ${(props) => props.heroImage ? rem(9) : rem(120)};
    }

    ${HeroAnimation},
    ${MobileHeroAnimation},
    ${HeroImage},
    ${MobileHeroImage} {
      max-width: ${rem(480)};
      margin: auto;
      width: 100%;
    }

    ${HeroAnimation},
    ${HeroImage} {
      margin: 0;
      display: none;

      ${media('>small')} {
        display: inherit;
      }

      ${media('>large')} {
        max-width: none;
      })
  }

  ${MobileHeroAnimation},
  ${MobileHeroImage} {
    ${media('>small')} {
      display: none;
    }
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${rem(40)};

  ${media('>small')} {
    width: ${rem(640)};
    max-width: 100%;
    padding-right: ${rem(24)};
    margin: auto;
  }

  ${ButtonGroup} {
    margin-top: ${rem(20)};
  }
  /* video button */
   ${ButtonText} {
    align-items: center;
    padding: 1rem 1.5rem;
    font-weight: 600;
    font-size: 1rem;
    line-height: 1.2;
    overflow: hidden;
    cursor: pointer;
    max-width: ${rem(238)};
  }
  > a[href*='hero-video'] {
      border-radius: 2.5rem;
      max-width: fit-content;
      margin-bottom: ${rem(20)};
      padding: 0.28rem 1rem;
      background-color: #ebeff2;
      color: #001a2d;
      font-size: smaller;
      text-decoration: none;
      text-transform: uppercase;
  }
`;

const Headline = styled.h1`
  ${headers.h2}
  color: ${colors.blue['800']};
`;

const Subhead = styled.p`
  ${body.regular}
  margin-top: ${rem(16)};
  color: ${colors.gray['600']};;
`;

const Annotation = styled.div`
  margin-top: ${rem(48)};
  ${({ videoUrl }) => videoUrl && `
      margin-top: ${rem(16)};
    `}
`;

const AnnotationImage = styled(GatsbyImage)`
  margin-top: ${rem(16)};
  max-width: ${rem(340)};
`;

const Hero = ({
  useH1,
  headline,
  subhead,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  annotationImage,
  heroImage,
  videoUrl,
  videoTitle,
  videoPlayButton
}) => (
  <Container>
    <ContentWrapper heroImage={heroImage}>
      {!heroImage && (
        <MobileHeroAnimation className="diffy-ignore" />
      )}
      {heroImage && (
        <MobileHeroImage className="diffy-ignore" image={getImage(heroImage)} />
      )}
      <TextContent videoPlayButton={videoPlayButton}>
        {videoUrl && (
          <ButtonText to={videoUrl} color="dark" type="link" videoPlayButton={videoPlayButton}>
            <TextRenderer text={videoTitle} />
            <VideoPlayButton image={videoPlayButton} />
          </ButtonText>
        )}
        {headline && (
          <Headline as={useH1 ? 'h1' : 'h2'}>
            <TextRenderer text={headline} />
          </Headline>
        )}
        {subhead && (
          <Subhead>
            <TextRenderer text={subhead} />
          </Subhead>
        )}
        <ButtonGroup>
          {primaryCtaText && primaryCtaText && (
            <Button to={primaryCtaLink}>
              <TextRenderer text={primaryCtaText} />
            </Button>
          )}
          {secondaryCtaText && secondaryCtaText && (
            <ButtonText color="dark" to={secondaryCtaLink}>
              <TextRenderer text={secondaryCtaText} />
            </ButtonText>
          )}
        </ButtonGroup>
        {annotationImage && (
        <Annotation videoUrl={videoUrl}>
          <AnnotationImage
            image={getImage(annotationImage)}
            alt={annotationImage?.description}
            objectFit="contain"
            objectPosition="left top"
            loading="eager"
          />
        </Annotation>
        )}
      </TextContent>
      {!heroImage && (
        <HeroAnimation className="diffy-ignore" />
      )}
      {heroImage && (
        <ImageContainer>
          <HeroImage image={getImage(heroImage)} />
        </ImageContainer>
      )}
    </ContentWrapper>
  </Container>
);

Hero.propTypes = {
  useH1: PropTypes.bool,
  headline: PropTypes.string,
  subhead: PropTypes.string,
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  annotationImage: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  heroImage: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  videoPlayButton: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  videoUrl: PropTypes.string
};

export const query = graphql`
  fragment HeroFragment on ContentfulHero {
    id
    __typename
    headline
    subhead
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
    annotationImage {
      gatsbyImageData(quality: 75)
      description
    }
    heroImage {
      gatsbyImageData(width: 1300, quality: 75)
      description
    }
    videoUrl
    videoTitle
    videoPlayButton {
      gatsbyImageData(width: 200, quality: 75)
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Hero;
